<template>
  <div
    ref="backgroundImageRef"
    :class="{
      [lightTextClass]: true,
      'background-image-wrapper': true,
    }"
    :style="backgroundImageClass"
  >
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';
import type { ImageBackground } from '~/sanity/types';
type ExtraProps = {
  // only use above the fold
  preload?: boolean;
  backgroundSize: 'full-page' | 'banner';
};
const props = defineProps<ImageBackground & ExtraProps>();
const { background, lightText } = toRefs(props);

// only preload above the fold, the parent component is responsible for setting this
if (props.preload && background.value && background.value.asset?._ref) {
  preloadImgSrc(cdnImage(background.value.asset._ref).bg);
}

const isVisible = ref(false);
const backgroundImageRef = useTemplateRef<HTMLElement>('backgroundImageRef');
const { stop } = useIntersectionObserver(
  backgroundImageRef,
  ([entry]) => {
    // once it's visible, keep it visible
    isVisible.value = isVisible.value || (entry?.isIntersecting ?? false);
    if (isVisible.value) {
      stop();
    }
  },
  {
    threshold: 0.01,
    rootMargin: '250px',
  }
);

const backgroundImageClass = computed(() => {
  if (!background.value || background.value._type !== 'image' || !background.value.asset?._ref) {
    return '';
  }
  if (!isVisible.value) {
    return ''; // in theory we could use a 1px placeholder here
  }
  if (props.backgroundSize === 'banner') {
    return `background-image : url(${cdnImage(background.value.asset._ref).bannerBg}); min-height: 150px; background-size: cover; background-repeat: no-repeat;`;
  } else if (props.backgroundSize === 'full-page') {
    return `background-image : url(${cdnImage(background.value.asset._ref).bg}); min-height: 150px; background-size: cover; background-repeat: no-repeat;`;
  }
  return '';
});
const lightTextClass = computed(() => {
  return lightText.value ? 'text-www-color-white' : 'text-www-color-neutral-medium-dark';
});
</script>

<style scoped></style>
